import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useFeature } from "../context/FeatureContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { PWAInstallBanner } from "../components/PWAInstall";
import { 
  Package, 
  FileText, 
  Tag,
  Wallet,
  AlertTriangle,
  Calendar,
  TrendingUp,
  TrendingDown,
  Banknote,
  CreditCard,
  Truck,
  PiggyBank,
  ArrowRight,
  Percent
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, LineChart, Line, XAxis, YAxis, CartesianGrid, BarChart, Bar } from 'recharts';
import NotificationCenter from "../components/dashboard/NotificationCenter";
import { CostAlertCard } from "../components/CostAlerts";
import DateRangeFilter from "../components/DateRangeFilter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── CalendarioEmbed: mini-calendario turni per dashboard senza Acquisti ───
const CalendarioEmbed = ({ getAuthHeaders, localeCorrente }) => {
  const [turni, setTurni] = useState([]);
  const [eventi, setEventi] = useState([]);
  const [loadingCal, setLoadingCal] = useState(true);

  useEffect(() => {
    if (!localeCorrente?.id) {
      setTurni([]);
      setEventi([]);
      setLoadingCal(false);
      return;
    }
    const fetchCal = async () => {
      try {
        const headers = getAuthHeaders();
        const today = new Date();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay() + 1);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(endOfWeek.getDate() + 6);
        const from = startOfWeek.toISOString().split("T")[0];
        const to = endOfWeek.toISOString().split("T")[0];

        const todayStr = today.toISOString().split("T")[0];
        const [turniRes, eventiRes] = await Promise.all([
          axios.get(`${API}/personale/turni`, { headers, params: { locale_id: localeCorrente.id, data_inizio: from, data_fine: to } }).catch(() => ({ data: [] })),
          axios.get(`${API}/calendario-attivita`, { headers, params: { locale_id: localeCorrente.id, data: todayStr } }).catch(() => ({ data: {} })),
        ]);
        setTurni(turniRes.data || []);
        const daily = eventiRes.data || {};
        const activities = ["pulizie", "gestione", "preparazioni", "preparazioni_spontanee"]
          .flatMap(key => Array.isArray(daily[key]) ? daily[key] : []);
        setEventi(activities.map(item => ({ ...item, data: todayStr })));
      } catch {}
      setLoadingCal(false);
    };
    fetchCal();
  }, [localeCorrente?.id]);

  const days = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
  const today = new Date();
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay() + 1);

  return (
    <div className="bg-card rounded-2xl p-5 shadow-sm border border-border" data-testid="calendario-embed">
      <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
        <Calendar className="h-4 w-4 text-primary" />
        Settimana Corrente
      </h3>
      {loadingCal ? (
        <div className="text-center text-xs text-muted-foreground py-6">Caricamento...</div>
      ) : (
        <div className="grid grid-cols-7 gap-2">
          {days.map((day, i) => {
            const date = new Date(startOfWeek);
            date.setDate(date.getDate() + i);
            const dateStr = date.toISOString().split("T")[0];
            const isToday = dateStr === today.toISOString().split("T")[0];
            const dayTurni = turni.filter(t => t.data === dateStr);
            const dayEventi = eventi.filter(e => e.data === dateStr);
            const count = dayTurni.length + dayEventi.length;
            return (
              <div key={i} className={`text-center p-3 rounded-xl border transition-all ${isToday ? "bg-primary/10 border-primary/30" : "bg-muted/30 border-border"}`}>
                <p className={`text-[10px] font-bold uppercase ${isToday ? "text-primary" : "text-muted-foreground"}`}>{day}</p>
                <p className={`text-lg font-black mt-0.5 ${isToday ? "text-primary" : "text-foreground"}`}>{date.getDate()}</p>
                {count > 0 && (
                  <div className="mt-1.5 flex justify-center gap-0.5">
                    {dayTurni.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-blue-400" title={`${dayTurni.length} turni`} />}
                    {dayEventi.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" title={`${dayEventi.length} attivita`} />}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="flex gap-4 mt-3 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400" />Turni</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" />Attività di oggi</span>
      </div>
    </div>
  );
};

// Data inizio attività
const DATA_INIZIO_ATTIVITA = "2025-12-12";

// Giorni settimana in italiano (per il modal)
const GIORNI_SETTIMANA_FULL = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];

// Format date to Italian format: dd.mm.yy
const formatDateIT = (dateStr) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${day}.${month}.${year.slice(2)}`;
};

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
};

// Get Monday of a week for a given date
const getMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
  return new Date(d.setDate(diff));
};

// Get week key (YYYY-WW format)
const getWeekKey = (dateStr) => {
  const date = new Date(dateStr);
  const monday = getMonday(date);
  const year = monday.getFullYear();
  const firstMonday = getMonday(new Date(year, 0, 4)); // First Monday of year
  const weekNum = Math.ceil((monday - firstMonday) / (7 * 24 * 60 * 60 * 1000)) + 1;
  return `${year}-W${weekNum.toString().padStart(2, '0')}`;
};

const Dashboard = () => {
  const { getAuthHeaders, localeCorrente } = useAuth();
  const { hasFeature } = useFeature();
  const hasAcquisti = hasFeature("prima_nota");
  const [loading, setLoading] = useState(true);
  
  // Filtri data
  const [dataInizio, setDataInizio] = useState(DATA_INIZIO_ATTIVITA);
  const [dataFine, setDataFine] = useState(new Date().toISOString().split('T')[0]);
  
  // Modal per dettaglio giorno settimana
  const [selectedDay, setSelectedDay] = useState(null); // 'Lun', 'Mar', etc.
  
  // Dati
  const [entrate, setEntrate] = useState([]);
  const [uscite, setUscite] = useState([]);
  const [versamentiBanca, setVersamentiBanca] = useState([]);
  const [serviziDelivery, setServiziDelivery] = useState([]);
  const [lottiInScadenza, setLottiInScadenza] = useState([]);
  const [prodotti, setProdotti] = useState([]);
  const [tipologieUscita, setTipologieUscita] = useState([]);

  const fetchData = async () => {
    if (!localeCorrente) return;
    
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const localeParam = `locale_id=${localeCorrente.id}`;
      
      const [entrateRes, usciteRes, serviziRes, scadenzeRes, prodottiRes, tipologieRes, versamentiRes] = await Promise.all([
        axios.get(`${API}/entrate?data_inizio=${dataInizio}&data_fine=${dataFine}&${localeParam}`, { headers }),
        axios.get(`${API}/uscite?data_inizio=${dataInizio}&data_fine=${dataFine}&${localeParam}`, { headers }),
        axios.get(`${API}/servizi-delivery`, { headers }),
        axios.get(`${API}/lotti/scadenze?giorni=7&${localeParam}`, { headers }),
        axios.get(`${API}/prodotti`, { headers }),
        axios.get(`${API}/tipologie-uscita`, { headers }),
        axios.get(`${API}/versamenti-banca?data_da=${dataInizio}&data_a=${dataFine}&${localeParam}`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setEntrate(entrateRes.data);
      setUscite(usciteRes.data);
      setVersamentiBanca(versamentiRes.data);
      // Deduplicazione per nome (ogni locale ha lo stesso servizio → evita colonne doppie)
      const seenDelivery = new Set();
      setServiziDelivery(serviziRes.data.filter(s => seenDelivery.has(s.nome) ? false : seenDelivery.add(s.nome)));
      setTipologieUscita(tipologieRes.data);
      setProdotti(prodottiRes.data);
      
      // Lotti con nome prodotto
      const prodottiMap = {};
      prodottiRes.data.forEach(p => { prodottiMap[p.id] = p.nome; });
      setLottiInScadenza(scadenzeRes.data.map(l => ({
        ...l,
        prodotto_nome: prodottiMap[l.prodotto_id] || "Prodotto sconosciuto"
      })));
      
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dataInizio, dataFine, localeCorrente]);

  // Calcoli
  const stats = useMemo(() => {
    // Mappa commissioni delivery
    const commissioniMap = {};
    serviziDelivery.forEach(s => {
      commissioniMap[s.nome] = s.commissione_percentuale || 0;
    });
    const deliveryNames = serviziDelivery.map(s => s.nome);

    // Percentuale commissione valida ALLA DATA dell'ordine (usa lo storico date-based).
    // Ogni voce {percentuale, dal} vale dalla sua data in poi; si sceglie l'ultima
    // con dal <= data ordine. La riga "2000-01-01" è la baseline iniziale.
    const commPercForDate = (servizio, dateStr) => {
      if (!servizio) return 0;
      const storico = (servizio.storico_commissioni || [])
        .slice()
        .sort((a, b) => (a.dal || "").localeCompare(b.dal || ""));
      if (storico.length === 0) return servizio.commissione_percentuale || 0;
      const d = (dateStr || "").slice(0, 10);
      let perc = storico[0].percentuale;
      for (const h of storico) {
        if ((h.dal || "").slice(0, 10) <= d) perc = h.percentuale;
        else break;
      }
      return perc || 0;
    };
    const servizioByNome = {};
    serviziDelivery.forEach(s => { servizioByNome[s.nome] = s; });
    
    // Calcoli entrate
    let totaleContanti = 0;
    let totalePOS = 0;
    let totaleDelivery = 0;
    let commissioniDelivery = 0;
    
    entrate.forEach(e => {
      const metodo = (e.metodo || "").toLowerCase().trim();
      if (metodo === "contanti") {
        totaleContanti += e.importo;
      } else if (metodo === "pos") {
        totalePOS += e.importo;
      } else {
        // Delivery: matcha sia "Deliveroo" che "Deliveroo CASH" ecc.
        const isDelivery = deliveryNames.some(name => {
          const n = name.toLowerCase();
          return metodo === n || metodo === `${n} cash` || metodo === `${n} pos`;
        });
        if (isDelivery) {
          totaleDelivery += e.importo;
          const baseName = deliveryNames.find(name => metodo.startsWith(name.toLowerCase()));
          const deliveryMatch = baseName ? servizioByNome[baseName] : null;
          const commPerc = deliveryMatch ? commPercForDate(deliveryMatch, e.data) : 0;
          const commBase = e.importo * (commPerc / 100);
          commissioniDelivery += commBase * 1.22;
        }
      }
    });
    
    const entrateLorde = totaleContanti + totalePOS + totaleDelivery;
    const entrateNette = entrateLorde - commissioniDelivery;
    const usciteTotali = uscite.reduce((s, u) => s + u.importo, 0);
    const rimanenza = entrateNette - usciteTotali;
    
    // Calcolo numero di settimane e mesi nel periodo (basato sui giorni effettivi)
    const d1 = new Date(dataInizio);
    const d2 = new Date(dataFine);
    const giorniTotali = Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24)) + 1;
    const numSettimane = Math.max(1, Math.round(giorniTotali / 7));
    
    // Calcolo mesi nel periodo
    const mesi1 = d1.getFullYear() * 12 + d1.getMonth();
    const mesi2 = d2.getFullYear() * 12 + d2.getMonth();
    const numMesi = Math.max(1, mesi2 - mesi1 + 1);
    
    // Media settimanale = totale entrate lorde / numero settimane
    const mediaSettimanale = entrateLorde / numSettimane;
    
    // Media mensile = totale entrate lorde / numero mesi
    const mediaMensile = entrateLorde / numMesi;
    
    // Calcolo media per giorno della settimana (0=Dom, 1=Lun, ..., 6=Sab) - INCASSI LORDI
    const incassiPerGiornoSettimana = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
    const entratePerData = {};
    
    // Raggruppa entrate per data - IMPORTI LORDI
    entrate.forEach(e => {
      if (!entratePerData[e.data]) {
        entratePerData[e.data] = 0;
      }
      // Usa importo LORDO (senza sottrarre commissioni)
      entratePerData[e.data] += e.importo;
    });
    
    // Conta i giorni con incasso > 0 (giorni aperti) per la media giornaliera
    const giorniAperti = Object.values(entratePerData).filter(importo => importo > 0).length;
    const mediaGiornaliera = giorniAperti > 0 ? entrateLorde / giorniAperti : 0;
    
    // Distribuisci per giorno della settimana - escludendo giorni con incasso <= 0 (chiusura)
    Object.entries(entratePerData).forEach(([data, importo]) => {
      if (importo > 0) { // Escludi giorni chiusi
        const dayOfWeek = new Date(data).getDay(); // 0=Dom, 1=Lun, etc.
        incassiPerGiornoSettimana[dayOfWeek].push(importo);
      }
    });
    
    // Calcola medie per ogni giorno (solo giorni aperti)
    const mediaPerGiorno = {};
    const giorniNomi = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
    giorniNomi.forEach((nome, index) => {
      const values = incassiPerGiornoSettimana[index];
      mediaPerGiorno[nome] = values.length > 0 
        ? values.reduce((a, b) => a + b, 0) / values.length 
        : 0;
    });
    
    // Calcolo Fondo Cassa Finale dell'ultimo giorno
    // Formula: FondoCassaFinale = FondoIniziale + Contanti + DeliveryCash - UsciteContanti - VersamentiBanca
    const FONDO_CASSA_INIZIALE = 50;
    const dateSet = new Set([...entrate.map(e => e.data), ...uscite.map(u => u.data), ...versamentiBanca.map(v => v.data)]);
    const dateArray = Array.from(dateSet).sort();
    
    let fondoCassaFinale = FONDO_CASSA_INIZIALE;
    dateArray.forEach(data => {
      // Entrate contanti del giorno (case-insensitive)
      const contantiGiorno = entrate
        .filter(e => e.data === data && (e.metodo || "").toLowerCase() === "contanti")
        .reduce((s, e) => s + e.importo, 0);
      
      // Delivery CASH del giorno (quelli con "CASH" nel nome)
      const deliveryCashGiorno = entrate
        .filter(e => e.data === data && (e.metodo || "").toUpperCase().includes("CASH"))
        .reduce((s, e) => s + e.importo, 0);
      
      // Uscite contanti del giorno (case-insensitive)
      const usciteContantiGiorno = uscite
        .filter(u => u.data === data && ["contanti","contante"].includes((u.metodo || "").toLowerCase()))
        .reduce((s, u) => s + u.importo, 0);

      // Versamenti in banca del giorno
      const versamentiGiorno = versamentiBanca
        .filter(v => v.data === data)
        .reduce((s, v) => s + v.importo, 0);
      
      fondoCassaFinale += contantiGiorno + deliveryCashGiorno - usciteContantiGiorno - versamentiGiorno;
    });
    
    // Delivery per singolo servizio
    const deliveryPerServizio = {};
    entrate.forEach(e => {
      const metodo = (e.metodo || "").toLowerCase().trim();
      deliveryNames.forEach(name => {
        const n = name.toLowerCase();
        if (metodo === n || metodo === `${n} cash` || metodo === `${n} pos`) {
          if (!deliveryPerServizio[name]) {
            deliveryPerServizio[name] = {
              nome: name, lordo: 0, commissioni: 0, netto: 0,
              commPerc: commissioniMap[name] || 0
            };
          }
          deliveryPerServizio[name].lordo += e.importo;
          const commPerc = commPercForDate(servizioByNome[name], e.data);
          const commBase = e.importo * (commPerc / 100);
          deliveryPerServizio[name].commissioni += commBase * 1.22;
        }
      });
    });
    Object.values(deliveryPerServizio).forEach(d => { d.netto = d.lordo - d.commissioni; });

    // Trend mensile delivery per servizio (ultimi 4 mesi)
    const deliveryMensile = {};
    entrate.forEach(e => {
      const metodo = (e.metodo || "").toLowerCase().trim();
      deliveryNames.forEach(name => {
        const n = name.toLowerCase();
        if (metodo === n || metodo === `${n} cash` || metodo === `${n} pos`) {
          const [y, m] = (e.data || "").split("-");
          const key = `${y}-${m}`;
          if (!deliveryMensile[key]) deliveryMensile[key] = {};
          deliveryMensile[key][name] = (deliveryMensile[key][name] || 0) + e.importo;
        }
      });
    });
    const deliveryTrend = Object.entries(deliveryMensile)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-5)
      .map(([key, vals]) => {
        const [, m] = key.split("-");
        const mesiNomi = ["","Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
        return { mese: mesiNomi[parseInt(m)] || m, ...vals };
      });

    return {
      totaleContanti,
      totalePOS,
      totaleDelivery,
      entrateLorde,
      entrateNette,
      commissioniDelivery,
      usciteTotali,
      rimanenza,
      mediaGiornaliera,
      mediaSettimanale,
      mediaMensile,
      numSettimane,
      numMesi,
      giorniAperti,
      fondoCassaFinale,
      mediaPerGiorno,
      deliveryPerServizio,
      deliveryTrend,
    };
  }, [entrate, uscite, versamentiBanca, serviziDelivery, dataInizio, dataFine]);

  // Dati dettagliati per il modal del giorno selezionato
  const dettaglioGiornoSelezionato = useMemo(() => {
    if (!selectedDay) return [];
    
    const giorniMap = { 'Dom': 0, 'Lun': 1, 'Mar': 2, 'Mer': 3, 'Gio': 4, 'Ven': 5, 'Sab': 6 };
    const targetDayIndex = giorniMap[selectedDay];
    
    // Mappa commissioni
    const commissioniMap = {};
    serviziDelivery.forEach(s => {
      commissioniMap[s.nome] = s.commissione_percentuale || 0;
    });
    const deliveryNames = serviziDelivery.map(s => s.nome);
    
    // Raggruppa entrate per data - IMPORTI LORDI
    const entratePerData = {};
    entrate.forEach(e => {
      const dayOfWeek = new Date(e.data).getDay();
      if (dayOfWeek === targetDayIndex) {
        if (!entratePerData[e.data]) {
          entratePerData[e.data] = { data: e.data, totale: 0, dettaglio: [] };
        }
        
        // Usa importo LORDO
        entratePerData[e.data].totale += e.importo;
        entratePerData[e.data].dettaglio.push({
          metodo: e.metodo,
          importo: e.importo // Importo lordo
        });
      }
    });
    
    // Converti in array, filtra giorni chiusi (totale <= 0) e ordina per data
    return Object.values(entratePerData)
      .filter(d => d.totale > 0) // Escludi giorni chiusi
      .sort((a, b) => a.data.localeCompare(b.data));
  }, [selectedDay, entrate, serviziDelivery]);

  // Dati per grafico a torta entrate
  const pieData = useMemo(() => {
    return [
      { name: 'Contanti', value: stats.totaleContanti, color: '#22c55e' },
      { name: 'POS', value: stats.totalePOS, color: '#3b82f6' },
      { name: 'Delivery', value: stats.totaleDelivery, color: '#f59e0b' },
    ].filter(d => d.value > 0);
  }, [stats]);

  // Dati per grafico a torta spese per tipologia
  const pieDataSpese = useMemo(() => {
    const colori = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899'];
    const tipologieMap = {};
    const coloreByNome = {};
    tipologieUscita.forEach(t => { tipologieMap[t.id] = t.nome; if (t.colore) coloreByNome[t.nome] = t.colore; });
    
    // Raggruppa uscite per tipologia
    const totaliPerTipologia = {};
    uscite.forEach(u => {
      const tipNome = tipologieMap[u.tipologia_id] || 'Altro';
      totaliPerTipologia[tipNome] = (totaliPerTipologia[tipNome] || 0) + parseFloat(u.importo);
    });
    
    // Converti in array per il grafico
    return Object.entries(totaliPerTipologia)
      .map(([name, value], index) => ({
        name,
        value: parseFloat(value.toFixed(2)),
        color: coloreByNome[name] || colori[index % colori.length]
      }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [uscite, tipologieUscita]);

  // Stato per le tipologie selezionate nel grafico lineare
  const [selectedTipologie, setSelectedTipologie] = useState(['entrate', 'uscite_totali']);
  
  // Stato per la vista del grafico (giornaliera/settimanale/mensile)
  const [chartView, setChartView] = useState('giornaliero');
  const [deliveryChartView, setDeliveryChartView] = useState('mensile');
  // null = non ancora inizializzato (= tutti accesi); array = lista dei servizi attivi
  const [deliveryActiveServices, setDeliveryActiveServices] = useState(null);

  // Dati per il grafico lineare andamento entrate/uscite nel tempo
  const lineChartData = useMemo(() => {
    // Crea mappa tipologie
    const tipologieMap = {};
    tipologieUscita.forEach(t => { tipologieMap[t.id] = t.nome; });
    
    // Raggruppa per data
    const dataMap = {};
    
    // Helper per formattare data come YYYY-MM-DD senza problemi di timezone
    const formatDateKey = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    // Funzione per ottenere la chiave di raggruppamento in base alla vista
    const getGroupKey = (dateStr) => {
      // Parse della data evitando problemi di timezone
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      
      if (chartView === 'giornaliero') {
        return dateStr;
      } else if (chartView === 'settimanale') {
        // Raggruppa per settimana (lunedì come inizio)
        const dayOfWeek = date.getDay();
        const diff = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        const monday = new Date(date.getFullYear(), date.getMonth(), diff);
        return formatDateKey(monday);
      } else if (chartView === 'mensile') {
        // Raggruppa per mese - usa il primo giorno del mese
        return `${year}-${String(month).padStart(2, '0')}-01`;
      }
      return dateStr;
    };
    
    // Funzione per formattare l'etichetta in base alla vista
    const getLabel = (key) => {
      const [year, month, day] = key.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      
      if (chartView === 'giornaliero') {
        return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
      } else if (chartView === 'settimanale') {
        return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
      } else if (chartView === 'mensile') {
        return date.toLocaleDateString('it-IT', { month: 'short', year: '2-digit' });
      }
      return key;
    };
    
    // Inizializza tutte le date/periodi nel range
    const [startYear, startMonth, startDay] = dataInizio.split('-').map(Number);
    const [endYear, endMonth, endDay] = dataFine.split('-').map(Number);
    const start = new Date(startYear, startMonth - 1, startDay);
    const end = new Date(endYear, endMonth - 1, endDay);
    
    if (chartView === 'giornaliero') {
      let current = new Date(start);
      while (current <= end) {
        const key = formatDateKey(current);
        dataMap[key] = {
          data: key,
          dataLabel: getLabel(key),
          entrate: 0,
          uscite_totali: 0
        };
        tipologieUscita.forEach(t => {
          dataMap[key][`tip_${t.id}`] = 0;
        });
        current.setDate(current.getDate() + 1);
      }
    } else if (chartView === 'settimanale') {
      // Trova il primo lunedì nel range
      let current = new Date(start);
      const dayOfWeek = current.getDay();
      const diff = current.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      current = new Date(current.getFullYear(), current.getMonth(), diff);
      
      while (current <= end) {
        const key = formatDateKey(current);
        dataMap[key] = {
          data: key,
          dataLabel: getLabel(key),
          entrate: 0,
          uscite_totali: 0
        };
        tipologieUscita.forEach(t => {
          dataMap[key][`tip_${t.id}`] = 0;
        });
        current.setDate(current.getDate() + 7);
      }
    } else if (chartView === 'mensile') {
      let current = new Date(start.getFullYear(), start.getMonth(), 1);
      while (current <= end) {
        const key = formatDateKey(current);
        dataMap[key] = {
          data: key,
          dataLabel: getLabel(key),
          entrate: 0,
          uscite_totali: 0
        };
        tipologieUscita.forEach(t => {
          dataMap[key][`tip_${t.id}`] = 0;
        });
        current.setMonth(current.getMonth() + 1);
      }
    }
    
    // Somma entrate per periodo
    entrate.forEach(e => {
      const key = getGroupKey(e.data);
      if (dataMap[key]) {
        dataMap[key].entrate += e.importo;
      }
    });
    
    // Somma uscite per periodo e per tipologia
    uscite.forEach(u => {
      const key = getGroupKey(u.data);
      if (dataMap[key]) {
        dataMap[key].uscite_totali += u.importo;
        if (u.tipologia_id) {
          dataMap[key][`tip_${u.tipologia_id}`] = (dataMap[key][`tip_${u.tipologia_id}`] || 0) + u.importo;
        }
      }
    });
    
    // Converti in array e ordina per data
    return Object.values(dataMap).sort((a, b) => a.data.localeCompare(b.data));
  }, [entrate, uscite, tipologieUscita, dataInizio, dataFine, chartView]);

  // Colori per le linee del grafico
  const lineColors = {
    entrate: '#22c55e',
    uscite_totali: '#ef4444'
  };
  const tipologieColors = ['#f97316', '#eab308', '#84cc16', '#14b8a6', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#f43f5e', '#a855f7'];

  // Grafico andamento delivery per servizio (usa stesse date di sopra, toggle proprio)
  const deliveryLineData = useMemo(() => {
    if (!serviziDelivery.length) return [];

    const formatDateKey = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    const getGroupKey = (dateStr) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      if (deliveryChartView === 'giornaliero') return dateStr;
      if (deliveryChartView === 'settimanale') {
        const dow = date.getDay();
        const diff = date.getDate() - dow + (dow === 0 ? -6 : 1);
        const monday = new Date(date.getFullYear(), date.getMonth(), diff);
        return formatDateKey(monday);
      }
      return `${year}-${String(month).padStart(2, '0')}-01`;
    };
    const getLabel = (key) => {
      const [y, m, d] = key.split('-').map(Number);
      const date = new Date(y, m - 1, d);
      if (deliveryChartView === 'giornaliero') return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
      if (deliveryChartView === 'settimanale') return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
      return date.toLocaleDateString('it-IT', { month: 'short', year: '2-digit' });
    };

    // Inizializza dataMap
    const dataMap = {};
    const [sy, sm, sd] = dataInizio.split('-').map(Number);
    const [ey, em, ed] = dataFine.split('-').map(Number);
    const start = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);

    if (deliveryChartView === 'giornaliero') {
      let cur = new Date(start);
      while (cur <= end) {
        const key = formatDateKey(cur);
        dataMap[key] = { data: key, dataLabel: getLabel(key) };
        serviziDelivery.forEach(s => { dataMap[key][s.nome] = 0; });
        cur.setDate(cur.getDate() + 1);
      }
    } else if (deliveryChartView === 'settimanale') {
      let cur = new Date(start);
      const dow = cur.getDay();
      const diff = cur.getDate() - dow + (dow === 0 ? -6 : 1);
      cur = new Date(cur.getFullYear(), cur.getMonth(), diff);
      while (cur <= end) {
        const key = formatDateKey(cur);
        dataMap[key] = { data: key, dataLabel: getLabel(key) };
        serviziDelivery.forEach(s => { dataMap[key][s.nome] = 0; });
        cur.setDate(cur.getDate() + 7);
      }
    } else {
      let cur = new Date(start.getFullYear(), start.getMonth(), 1);
      while (cur <= end) {
        const key = formatDateKey(cur);
        dataMap[key] = { data: key, dataLabel: getLabel(key) };
        serviziDelivery.forEach(s => { dataMap[key][s.nome] = 0; });
        cur.setMonth(cur.getMonth() + 1);
      }
    }

    // Somma entrate delivery raggruppate per servizio (POS + CASH insieme)
    entrate.forEach(e => {
      const metodo = (e.metodo || '').toLowerCase().trim();
      serviziDelivery.forEach(s => {
        const n = s.nome.toLowerCase();
        if (metodo === n || metodo === `${n} cash` || metodo === `${n} pos`) {
          const key = getGroupKey(e.data);
          if (dataMap[key]) dataMap[key][s.nome] = (dataMap[key][s.nome] || 0) + e.importo;
        }
      });
    });

    return Object.values(dataMap).sort((a, b) => a.data.localeCompare(b.data));
  }, [entrate, serviziDelivery, dataInizio, dataFine, deliveryChartView]);

  // Servizi delivery attivi (default: tutti)
  const activeDeliveryServices = useMemo(
    () => deliveryActiveServices ?? serviziDelivery.map(s => s.nome),
    [deliveryActiveServices, serviziDelivery]
  );
  const toggleDeliveryService = (nome) => {
    const current = deliveryActiveServices ?? serviziDelivery.map(s => s.nome);
    if (current.includes(nome)) {
      if (current.length > 1) setDeliveryActiveServices(current.filter(n => n !== nome));
    } else {
      setDeliveryActiveServices([...current, nome]);
    }
  };

  // Totale aggregato delivery (sum di tutti i servizi)
  const deliveryTotale = useMemo(() => {
    const vals = Object.values(stats.deliveryPerServizio);
    return {
      lordo: vals.reduce((s, d) => s + d.lordo, 0),
      commissioni: vals.reduce((s, d) => s + d.commissioni, 0),
      netto: vals.reduce((s, d) => s + d.netto, 0),
    };
  }, [stats.deliveryPerServizio]);

  // Nome completo del giorno selezionato
  const giornoFullName = useMemo(() => {
    const giorniMap = { 'Dom': 'Domenica', 'Lun': 'Lunedì', 'Mar': 'Martedì', 'Mer': 'Mercoledì', 'Gio': 'Giovedì', 'Ven': 'Venerdì', 'Sab': 'Sabato' };
    return giorniMap[selectedDay] || '';
  }, [selectedDay]);

  const quickActions = [
    { to: "/prodotti", icon: Package, label: "Prodotti", color: "bg-blue-500/20 text-blue-400" },
    { to: "/fatture", icon: FileText, label: "Fattura", color: "bg-lime-500/20 text-lime-400" },
    { to: "/etichette", icon: Tag, label: "Lotti", color: "bg-purple-500/20 text-purple-400" },
    { to: "/prima-nota", icon: Wallet, label: "Cassa & Contabilità", color: "bg-amber-500/20 text-amber-400" },
  ];

  const CARD = { backgroundColor: "#fff", borderRadius: "20px", boxShadow: "0 2px 16px rgba(108,99,255,0.07)", border: "none" };
  const CARD_P = "p-5";
  const LABEL_SM = "text-xs font-medium text-muted-foreground";
  const VAL_LG = "text-2xl font-bold mt-1";

  // ─── Dashboard senza pacchetto Acquisti → Calendario + Alert ───
  if (!hasAcquisti) {
    return (
      <div className="page-container" data-testid="dashboard">
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="page-title">Dashboard</h1>
            <p className="page-subtitle">Calendario e notifiche</p>
          </div>
        </div>

        {/* Notification Center (sempre visibile) */}
        <NotificationCenter />

        {/* Embedded Calendar */}
        <div className="mt-4">
          <CalendarioEmbed getAuthHeaders={getAuthHeaders} localeCorrente={localeCorrente} />
        </div>

        {/* Alert Scadenze (sempre visibili) */}
        {lottiInScadenza.length > 0 && (
          <div className={CARD_P} style={{ ...CARD, backgroundColor: "#FEF2F2", marginTop: "16px" }}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-xl bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 text-red-500" />
              </div>
              <h3 className="text-sm font-semibold text-red-500">Prodotti in Scadenza (7 giorni)</h3>
            </div>
            <ul className="space-y-2">
              {lottiInScadenza.slice(0, 5).map((lotto) => (
                <li key={lotto.id}>
                  <Link to={`/lotti?lotto_id=${lotto.id}`} className="flex items-center justify-between text-sm hover:bg-red-50 rounded-lg px-2 py-1.5 -mx-2 transition-colors">
                    <span className="font-medium text-foreground">{lotto.prodotto_nome}</span>
                    <div className="flex items-center gap-2 text-red-400">
                      <Calendar className="h-3.5 w-3.5" />
                      <span className="text-xs font-semibold">{formatDateIT(lotto.data_scadenza)}</span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="dashboard">
      <PWAInstallBanner area="admin" />
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Panoramica finanziaria</p>
        </div>

        {/* Filtri Data */}
        <DateRangeFilter
          dataInizio={dataInizio}
          dataFine={dataFine}
          onRangeChange={(start, end) => { setDataInizio(start); setDataFine(end); }}
          data-testid="dashboard-date-filter"
        />
      </div>

      {/* Notification Center */}
      <NotificationCenter />

      {/* Allerta costi / food cost */}
      <CostAlertCard localeId={localeCorrente?.id} />

      {/* Grafico Lineare Andamento Entrate/Uscite */}
      <div className={CARD_P} style={CARD}>
        <div className="flex flex-col gap-3 mb-4">
          {/* Header con titolo e selettore vista */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h3 className="text-sm font-semibold text-foreground">Andamento Entrate e Uscite</h3>
            {/* Selettore Vista */}
            <div className="flex items-center gap-1 bg-muted p-1 rounded-xl">
              {[
                { value: 'giornaliero', label: 'Giorno' },
                { value: 'settimanale', label: 'Settimana' },
                { value: 'mensile', label: 'Mese' }
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setChartView(value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                    chartView === value 
                      ? 'bg-card text-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Toggle serie dati */}
          <div className="flex flex-wrap gap-2">
            {/* Toggle Entrate */}
            <button
              onClick={() => setSelectedTipologie(prev => 
                prev.includes('entrate') ? prev.filter(t => t !== 'entrate') : [...prev, 'entrate']
              )}
              className={`px-3 py-1 text-xs rounded-full border transition-all ${
                selectedTipologie.includes('entrate') 
                  ? 'bg-green-500 text-white border-green-500' 
                  : 'bg-card text-muted-foreground border-border hover:border-green-300'
              }`}
            >
              Entrate
            </button>
            {/* Toggle Uscite Totali */}
            <button
              onClick={() => setSelectedTipologie(prev => 
                prev.includes('uscite_totali') ? prev.filter(t => t !== 'uscite_totali') : [...prev, 'uscite_totali']
              )}
              className={`px-3 py-1 text-xs rounded-full border transition-all ${
                selectedTipologie.includes('uscite_totali') 
                  ? 'bg-red-500 text-white border-red-500' 
                  : 'bg-card text-muted-foreground border-border hover:border-red-300'
              }`}
            >
              Uscite Totali
            </button>
            {/* Divider */}
            <div className="w-px h-6 bg-border hidden md:block" />
            {/* Toggle per ogni tipologia uscita */}
            {tipologieUscita.map((tip, idx) => (
              <button
                key={tip.id}
                onClick={() => setSelectedTipologie(prev => 
                  prev.includes(`tip_${tip.id}`) ? prev.filter(t => t !== `tip_${tip.id}`) : [...prev, `tip_${tip.id}`]
                )}
                className={`px-3 py-1 text-xs rounded-full border transition-all ${
                  selectedTipologie.includes(`tip_${tip.id}`) 
                    ? 'text-white border-transparent' 
                    : 'bg-card text-muted-foreground border-border hover:border-border'
                }`}
                style={selectedTipologie.includes(`tip_${tip.id}`) ? { backgroundColor: tip.colore || tipologieColors[idx % tipologieColors.length] } : {}}
              >
                {tip.nome}
              </button>
            ))}
          </div>
        </div>
        
        {loading ? (
          <div className="h-64 flex items-center justify-center text-muted-foreground/40">Caricamento...</div>
        ) : lineChartData.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-muted-foreground/40">Nessun dato</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={lineChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis 
                dataKey="dataLabel" 
                tick={{ fontSize: 10, fill: '#6B7280' }} 
                tickLine={false}
                axisLine={{ stroke: '#E5E7EB' }}
                interval={chartView === 'giornaliero' ? 'preserveStartEnd' : 0}
              />
              <YAxis 
                tick={{ fontSize: 10, fill: '#6B7280' }} 
                tickLine={false}
                axisLine={{ stroke: '#E5E7EB' }}
                tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}
              />
              <Tooltip 
                formatter={(value, name) => {
                  const formatted = formatCurrency(value);
                  let label = name;
                  if (name === 'entrate') label = 'Entrate';
                  else if (name === 'uscite_totali') label = 'Uscite Totali';
                  else if (name.startsWith('tip_')) {
                    const tipId = name.replace('tip_', '');
                    const tip = tipologieUscita.find(t => t.id === tipId);
                    label = tip?.nome || name;
                  }
                  return [formatted, label];
                }}
                labelFormatter={(label) => {
                  const viewLabel = chartView === 'giornaliero' ? 'Data' : chartView === 'settimanale' ? 'Settimana' : 'Mese';
                  return `${viewLabel}: ${label}`;
                }}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #E5E7EB', 
                  borderRadius: '12px', 
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  fontSize: 12
                }}
              />
              {/* Linea Entrate */}
              {selectedTipologie.includes('entrate') && (
                <Line 
                  type="monotone" 
                  dataKey="entrate" 
                  stroke={lineColors.entrate} 
                  strokeWidth={2}
                  dot={chartView !== 'giornaliero'}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                />
              )}
              {/* Linea Uscite Totali */}
              {selectedTipologie.includes('uscite_totali') && (
                <Line 
                  type="monotone" 
                  dataKey="uscite_totali" 
                  stroke={lineColors.uscite_totali} 
                  strokeWidth={2}
                  dot={chartView !== 'giornaliero'}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                />
              )}
              {/* Linee per ogni tipologia uscita */}
              {tipologieUscita.map((tip, idx) => (
                selectedTipologie.includes(`tip_${tip.id}`) && (
                  <Line 
                    key={tip.id}
                    type="monotone" 
                    dataKey={`tip_${tip.id}`} 
                    stroke={tip.colore || tipologieColors[idx % tipologieColors.length]} 
                    strokeWidth={1.5}
                    dot={chartView !== 'giornaliero'}
                    activeDot={{ r: 3, strokeWidth: 0 }}
                    strokeDasharray="5 5"
                  />
                )
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { icon: Banknote, label: "Fondo Cassa", value: stats.fondoCassaFinale, color: "#6C63FF", bg: "#EEF0FF", testid: "stat-fondo-cassa" },
          { icon: TrendingUp, label: "Entrate Lorde", value: stats.entrateLorde, color: "#10b981", bg: "#ECFDF5", testid: "stat-entrate-lorde" },
          { icon: PiggyBank, label: "Rimanenza", value: stats.rimanenza, color: "#f59e0b", bg: "#FFFBEB", testid: "stat-rimanenza" },
          { icon: TrendingDown, label: "Uscite Totali", value: stats.usciteTotali, color: "#ef4444", bg: "#FEF2F2", testid: "stat-uscite" },
        ].map(({ icon: Icon, label, value, color, bg, testid }) => (
          <div key={label} className={CARD_P} style={{ ...CARD, background: bg }}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
                <Icon className="h-4 w-4" style={{ color }} />
              </div>
              <span className={LABEL_SM} style={{ color }}>{label}</span>
            </div>
            <div className={VAL_LG} style={{ color }} data-testid={testid}>
              {loading ? "..." : formatCurrency(value)}
            </div>
          </div>
        ))}
      </div>

      {/* Delivery + Medie */}
      <div className="grid grid-cols-1 gap-3">
      {/* Andamento Delivery — card compatta stile light */}
      {stats.totaleDelivery > 0 && serviziDelivery.length > 0 && (
        <div className={CARD_P} style={CARD} data-testid="delivery-breakdown-card">

          {/* Header + toggle G/S/M */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Truck className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-semibold text-foreground">Andamento Delivery</span>
            </div>
            <div className="flex gap-1 bg-muted rounded-lg p-0.5">
              {[['giornaliero','G'],['settimanale','S'],['mensile','M']].map(([val, label]) => (
                <button key={val} onClick={() => setDeliveryChartView(val)}
                  className={`text-[10px] font-bold px-2.5 py-1 rounded-md transition-all ${deliveryChartView === val ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Riquadro totale aggregato (sempre visibile) */}
          <div className="rounded-xl p-3 mb-3" style={{ background: 'linear-gradient(135deg,#fffbeb,#fef3c7)' }}>
            <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide mb-1.5">Totale Delivery</p>
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="text-xl font-black text-gray-800">{formatCurrency(deliveryTotale.netto)}</span>
              <span className="text-xs text-gray-400">netto</span>
            </div>
            <div className="flex gap-3 mt-1">
              <span className="text-[10px] text-gray-500">lordo <span className="font-semibold text-gray-700">{formatCurrency(deliveryTotale.lordo)}</span></span>
              <span className="text-[10px] text-red-400">commissioni <span className="font-semibold">-{formatCurrency(deliveryTotale.commissioni)}</span></span>
            </div>
          </div>

          {/* Card singoli servizi — cliccabili (toggle on/off indipendenti) */}
          <div className="grid gap-2 mb-3" style={{ gridTemplateColumns: `repeat(${serviziDelivery.length}, 1fr)` }}>
            {Object.values(stats.deliveryPerServizio).map((serv, idx) => {
              const colors = ['#f59e0b','#8b5cf6'];
              const c = colors[idx % colors.length];
              const isOn = activeDeliveryServices.includes(serv.nome);
              return (
                <div key={serv.nome}
                  className="rounded-xl p-3 cursor-pointer transition-all select-none"
                  data-testid={`delivery-servizio-card-${serv.nome}`}
                  style={{ background: isOn ? '#f9fafb' : '#f3f4f6', opacity: isOn ? 1 : 0.4, borderWidth: 1.5, borderStyle: 'solid', borderColor: isOn ? c : 'transparent' }}
                  onClick={() => toggleDeliveryService(serv.nome)}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: c }} />
                    <span className="text-xs font-bold text-foreground truncate" data-testid={`delivery-servizio-nome-${serv.nome}`}>{serv.nome}</span>
                    <span className="text-[9px] text-muted-foreground ml-auto" data-testid={`delivery-servizio-perc-${serv.nome}`}>{serv.commPerc}%</span>
                  </div>
                  <p className="text-base font-black text-foreground" data-testid={`delivery-servizio-netto-${serv.nome}`}>{formatCurrency(serv.netto)}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    lordo <span data-testid={`delivery-servizio-lordo-${serv.nome}`}>{formatCurrency(serv.lordo)}</span> <span className="text-red-400" data-testid={`delivery-servizio-commissioni-${serv.nome}`}>-{formatCurrency(serv.commissioni)}</span>
                  </p>
                </div>
              );
            })}
          </div>

          {/* LineChart — mostra le linee dei soli servizi accesi */}
          {deliveryLineData.length > 1 && (
            <ResponsiveContainer width="100%" height={130}>
              <LineChart data={deliveryLineData} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="dataLabel"
                  tick={{ fontSize: 9, fill: '#9ca3af' }}
                  axisLine={false} tickLine={false}
                  interval={deliveryChartView === 'giornaliero' ? 'preserveStartEnd' : 0}
                />
                <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false}
                  tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v} />
                <Tooltip
                  formatter={(v, name) => [formatCurrency(v), name]}
                  contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', fontSize: 12 }}
                  labelStyle={{ fontWeight: 700, marginBottom: 4, fontSize: 11 }}
                />
                {serviziDelivery.map((s, idx) =>
                  activeDeliveryServices.includes(s.nome) ? (
                    <Line key={s.nome} type="monotone" dataKey={s.nome}
                      stroke={['#f59e0b','#8b5cf6'][idx % 2]}
                      strokeWidth={2} dot={deliveryChartView !== 'giornaliero'}
                      activeDot={{ r: 4 }} />
                  ) : null
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

        <div className={CARD_P} style={CARD}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-purple-50">
              <Calendar className="h-4 w-4 text-[#6C63FF]" />
            </div>
            <span className="text-sm font-semibold text-foreground">Medie Incassi Lordi</span>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className={LABEL_SM}>Media Giornaliera</div>
              <div className="text-lg font-bold mt-1" style={{ color: "#6C63FF" }}>{loading ? "..." : formatCurrency(stats.mediaGiornaliera)}</div>
              <div className="text-[10px] text-muted-foreground/50 mt-0.5">{stats.giorniAperti} giorni</div>
            </div>
            <div>
              <div className={LABEL_SM}>Media Settimanale</div>
              <div className="text-lg font-bold mt-1" style={{ color: "#6C63FF" }}>{loading ? "..." : formatCurrency(stats.mediaSettimanale)}</div>
              <div className="text-[10px] text-muted-foreground/50 mt-0.5">{stats.numSettimane} sett.</div>
            </div>
            <div>
              <div className={LABEL_SM}>Media Mensile</div>
              <div className="text-lg font-bold mt-1" style={{ color: "#6C63FF" }}>{loading ? "..." : formatCurrency(stats.mediaMensile)}</div>
              <div className="text-[10px] text-muted-foreground/50 mt-0.5">{stats.numMesi} mesi</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bar chart giorni */}
      <div className={CARD_P} style={CARD}>
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-foreground">Media Incassi Lordi per Giorno</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Tocca un giorno per vedere il dettaglio</p>
        </div>
        {loading ? (
          <div className="h-20 flex items-center justify-center text-muted-foreground/40">Caricamento...</div>
        ) : (
          <div className="flex flex-col sm:grid sm:grid-cols-7 gap-2.5 sm:gap-2 sm:items-end">
            {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((giorno) => {
              const media = stats.mediaPerGiorno?.[giorno] || 0;
              const maxMedia = Math.max(...Object.values(stats.mediaPerGiorno || {}));
              const percentage = maxMedia > 0 ? (media / maxMedia) * 100 : 0;
              const importo = formatCurrency(media).replace(' €', '');
              return (
                <div key={giorno}
                  className="group flex flex-row sm:flex-col items-center sm:justify-end gap-2.5 sm:gap-1 px-2 py-1.5 sm:px-1 sm:py-1 rounded-xl cursor-pointer transition-colors hover:bg-muted/40"
                  onClick={() => setSelectedDay(giorno)}
                  data-testid={`chart-bar-${giorno}`}>
                  {/* Mobile: etichetta giorno a sinistra */}
                  <div className="sm:hidden w-7 text-xs font-semibold text-muted-foreground shrink-0">{giorno}</div>

                  {/* Desktop: importo sopra la barra */}
                  <div className="hidden sm:block text-[11px] font-bold text-foreground mb-0.5 text-center w-full truncate">
                    {importo}
                  </div>

                  {/* Barra */}
                  <div className="flex-1 sm:flex-none sm:w-full h-7 sm:h-28 bg-muted rounded-lg relative overflow-hidden">
                    {/* Mobile: barra orizzontale */}
                    <div className="sm:hidden h-full rounded-lg transition-all duration-500"
                      style={{ width: `${Math.max(percentage, 2)}%`, background: 'linear-gradient(90deg, #818cf8, #6C63FF)' }} />
                    {/* Desktop: barra verticale */}
                    <div className="hidden sm:block absolute bottom-0 left-0 w-full rounded-t-lg transition-all duration-500"
                      style={{ height: `${Math.max(percentage, 2)}%`, background: 'linear-gradient(180deg, #818cf8, #6C63FF)' }} />
                  </div>

                  {/* Mobile: importo a destra */}
                  <div className="sm:hidden w-16 text-right text-xs font-bold text-foreground shrink-0">{importo}</div>

                  {/* Desktop: etichetta giorno sotto la barra */}
                  <div className="hidden sm:block text-[10px] font-semibold text-muted-foreground mt-0.5 text-center">{giorno}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Grafici Torta */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { title: "Composizione Fatturato", data: pieData, total: stats.entrateLorde, totalLabel: "Totale", totalColor: "#1A1A1E" },
          { title: "Composizione Spese", data: pieDataSpese, total: stats.usciteTotali, totalLabel: "Totale Spese", totalColor: "#ef4444" },
        ].map(({ title, data, total, totalLabel, totalColor }) => (
          <div key={title} className={CARD_P} style={CARD}>
            <h3 className="text-sm font-semibold text-foreground mb-4">{title}</h3>
            {loading ? (
              <div className="h-48 flex items-center justify-center text-muted-foreground/40">Caricamento...</div>
            ) : data.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-muted-foreground/40">Nessun dato</div>
            ) : (
              <div className="flex flex-col gap-4">
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={data} cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={2} dataKey="value"
                      label={({ name, percent, cx, cy, midAngle, outerRadius: or }) => {
                        const r = or + 22; const R = Math.PI / 180;
                        return <text x={cx + r * Math.cos(-midAngle * R)} y={cy + r * Math.sin(-midAngle * R)}
                          fill="#6B7280" textAnchor={cx + r * Math.cos(-midAngle * R) > cx ? 'start' : 'end'}
                          dominantBaseline="central" fontSize={10}>{`${name} ${(percent * 100).toFixed(0)}%`}</text>;
                      }}
                      labelLine={{ stroke: '#E5E7EB', strokeWidth: 1 }}>
                      {data.map((entry, index) => <Cell key={`c-${index}`} fill={entry.color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => { const p = v.toFixed(2).split('.'); return [p[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + p[1] + ' €']; }}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', color: '#1A1A1E', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1.5">
                  {data.map((item) => {
                    const tot = data.reduce((s, d) => s + d.value, 0);
                    const pct = tot > 0 ? ((item.value / tot) * 100).toFixed(0) : 0;
                    return (
                      <div key={item.name} className="flex items-center justify-between px-3 py-2 rounded-xl" style={{ backgroundColor: `${item.color}12` }}>
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                          <span className="text-xs font-medium text-gray-700 truncate max-w-[100px]">{item.name}</span>
                          <span className="text-[10px] text-gray-400">({pct}%)</span>
                        </div>
                        <span className="text-xs font-bold" style={{ color: item.color }}>{formatCurrency(item.value)}</span>
                      </div>
                    );
                  })}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100 px-3">
                    <span className="text-xs font-semibold text-gray-600">{totalLabel}</span>
                    <span className="text-sm font-bold" style={{ color: totalColor }}>{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Alert Scadenze */}
      {lottiInScadenza.length > 0 && (
        <div className={CARD_P} style={{ ...CARD, backgroundColor: "#FEF2F2" }}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </div>
            <h3 className="text-sm font-semibold text-red-500">Prodotti in Scadenza (7 giorni)</h3>
          </div>
          <ul className="space-y-2">
            {lottiInScadenza.slice(0, 5).map((lotto) => (
              <li key={lotto.id}>
                <Link to={`/lotti?lotto_id=${lotto.id}`} className="flex items-center justify-between text-sm hover:bg-red-50 rounded-lg px-2 py-1.5 -mx-2 transition-colors">
                  <span className="font-medium text-foreground">{lotto.prodotto_nome}</span>
                  <div className="flex items-center gap-2 text-red-400">
                    <Calendar className="h-3.5 w-3.5" />
                    <span className="text-xs font-semibold">{formatDateIT(lotto.data_scadenza)}</span>
                    {lotto.posizione && <span className="text-[10px] bg-red-100 text-red-500 px-2 py-0.5 rounded-lg">{lotto.posizione}</span>}
                  </div>
                </Link>
              </li>
            ))}
            {lottiInScadenza.length > 5 && (
              <li>
                <Link to="/lotti" className="text-xs text-red-400 font-semibold hover:text-red-600 transition-colors">
                  + altri {lottiInScadenza.length - 5} prodotti...
                </Link>
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground mb-3">Azioni Rapide</h2>
        <div className="grid grid-cols-4 gap-2">
          {quickActions.map((action) => (
            <Link key={action.to} to={action.to}>
              <div className="bg-card hover:bg-muted/30 rounded-2xl p-3 flex flex-col items-center text-center gap-2 transition-all active:scale-95 border border-border">
                <div className={`p-2.5 rounded-2xl ${action.color}`}>
                  <action.icon className="h-5 w-5" />
                </div>
                <span className="text-[10px] font-semibold text-muted-foreground leading-tight">{action.label}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Modal Dettaglio Giorno */}
      <Dialog open={!!selectedDay} onOpenChange={() => setSelectedDay(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col bg-card rounded-3xl border border-border" style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-foreground">Incassi Lordi del {giornoFullName}</DialogTitle>
            <p className="text-xs text-muted-foreground">Esclusi i giorni di chiusura</p>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {dettaglioGiornoSelezionato.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">Nessun incasso per questo giorno</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 border-b border-border">
                    <TableHead className="font-semibold text-muted-foreground">Data</TableHead>
                    <TableHead className="font-semibold text-muted-foreground">Dettaglio</TableHead>
                    <TableHead className="text-right font-semibold text-muted-foreground">Totale Lordo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dettaglioGiornoSelezionato.map((row) => (
                    <TableRow key={row.data} className="hover:bg-muted/20 border-b border-border/50">
                      <TableCell className="font-medium text-foreground">{formatDateIT(row.data)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {row.dettaglio.map((d, i) => (
                            <span key={i} className={`text-xs px-2 py-0.5 rounded-lg font-medium ${
                              d.metodo === 'Contanti' ? 'bg-green-100 text-green-600' :
                              d.metodo === 'POS' ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'
                            }`}>
                              {d.metodo}: {formatCurrency(d.importo)}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-bold" style={{ color: "#6C63FF" }}>{formatCurrency(row.totale)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="font-bold border-t border-border" style={{ backgroundColor: "#EEF0FF" }}>
                    <TableCell colSpan={2} className="text-foreground">TOTALE ({dettaglioGiornoSelezionato.length} {giornoFullName.toLowerCase()})</TableCell>
                    <TableCell className="text-right text-lg font-bold" style={{ color: "#6C63FF" }}>
                      {formatCurrency(dettaglioGiornoSelezionato.reduce((s, r) => s + r.totale, 0))}
                    </TableCell>
                  </TableRow>
                  <TableRow className="bg-muted/30">
                    <TableCell colSpan={2} className="font-medium text-muted-foreground">MEDIA LORDA per {giornoFullName}</TableCell>
                    <TableCell className="text-right font-bold text-foreground">{formatCurrency(stats.mediaPerGiorno?.[selectedDay] || 0)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
